import { useState } from 'react';
import { Tooltip } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';

// Stage 5N-2C: short copy-to-clipboard helper for long IDs (Razorpay
// payment id, order id, tenant uuid, request id). Renders the value
// in a <code> with an inline copy icon. On click, the icon flips to
// a checkmark for ~1.4s.
//
// Truncation is handled by `max` (default 18 chars). Pass max=0 to
// render the full string. The full value always shows in the
// tooltip on hover so the operator can read it before copying.

export default function CopyableText({
  value,
  max = 18,
  className = '',
  monospace = true
}) {
  const [copied, setCopied] = useState(false);

  if (!value) {
    return <span className="text-ink-400">—</span>;
  }

  const str = String(value);
  const display =
    max > 0 && str.length > max
      ? `${str.slice(0, Math.max(4, max - 4))}…${str.slice(-3)}`
      : str;

  async function copy(e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(str);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (_err) {
      /* noop — clipboard API can fail under iframe or insecure context */
    }
  }

  return (
    <Tooltip title={copied ? 'Copied' : str} mouseEnterDelay={0.3}>
      <span
        className={`inline-flex items-center gap-1 align-middle ${className}`}
      >
        {monospace ? (
          <code className="text-[11px] text-ink-700">{display}</code>
        ) : (
          <span className="text-xs text-ink-700">{display}</span>
        )}
        <button
          type="button"
          onClick={copy}
          aria-label="Copy"
          className="text-ink-400 hover:text-brand-500 active:text-brand-700 transition-colors"
        >
          {copied ? <CheckOutlined className="text-emerald-500" /> : <CopyOutlined />}
        </button>
      </span>
    </Tooltip>
  );
}
